// AI Recruiter panel — doporučení kandidátů pro Recruitment Focus.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Brain, Plus, Check, ArrowSquareOut, X, FloppyDisk } from "@phosphor-icons/react";
import { recruitFocus, focusRecommendations, addToPool, removePoolByFpl, saveRecruiterList, addCandidate } from "../../lib/lab-api";

const CAT = {
    best_fit: { label: "Best Fit", cls: "border-fa-mint/60 text-fa-mint bg-fa-mint/10" },
    safe_pick: { label: "Safe Pick", cls: "border-fa-sky/60 text-fa-sky bg-fa-sky/10" },
    upside_pick: { label: "Upside Pick", cls: "border-fa-yellow/60 text-fa-yellow bg-fa-yellow/10" },
    differential: { label: "Differential", cls: "border-fa-violet/80 text-fa-cream bg-fa-violet/20" },
    watch: { label: "Watch", cls: "border-white/30 text-white/60 bg-white/5" },
};

export default function RecruiterPanel({ focus, onClose, onPoolChange }) {
    const [data, setData] = useState(null);
    const [running, setRunning] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async () => {
        try {
            const d = await focusRecommendations(focus.id);
            setData(d);
            if (d.recruit_status === "running") {
                setRunning(true);
                timer.current = setTimeout(load, 3000);
            } else {
                setRunning(false);
                if (d.recruit_status === "failed" && d.recruit_error) toast.error(d.recruit_error);
            }
        } catch { setRunning(false); }
    }, [focus.id]);

    useEffect(() => {
        load();
        return () => clearTimeout(timer.current);
    }, [load]);

    const run = async () => {
        setRunning(true);
        try {
            await recruitFocus(focus.id);
            timer.current = setTimeout(load, 3000);
        } catch (e) {
            setRunning(false);
            toast.error(e.response?.data?.detail || "Spuštění selhalo");
        }
    };

    const toPool = async (it) => {
        try {
            await addToPool({ fpl_id: it.fpl_id, status: "scouted" });
            toast.success(`${it.name} přidán do poolu (Scouted)`);
            load();
            onPoolChange?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Přidání selhalo"); }
    };

    const fromPool = async (it) => {
        try {
            await removePoolByFpl(it.fpl_id);
            toast.success(`${it.name} vyřazen z poolu`);
            load();
            onPoolChange?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Vyřazení selhalo"); }
    };

    const toCandidate = async (it) => {
        try {
            const d = await addCandidate(focus.id, {
                fpl_id: it.fpl_id, source: "ai_recruiter",
                reason: (it.strengths || "").slice(0, 300) || null,
            });
            toast.success(`${it.name} → kandidát focusu (${d.active_count}/${d.max_active})`);
            onPoolChange?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Zařazení selhalo"); }
    };

    const items = data?.items || [];
    return (
        <div className="border border-fa-mint/40 bg-black/50 p-4 space-y-4" data-testid="recruiter-panel">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-fa-mint flex items-center gap-1.5">
                        <Brain size={12} /> AI Recruiter
                    </div>
                    <div className="font-display font-black uppercase text-base mt-0.5">{focus.title}</div>
                    <div className="font-mono text-[9px] text-white/45 uppercase mt-0.5">
                        GW{focus.gw_start}–{focus.gw_end}
                        {focus.position_filters?.length ? ` · ${focus.position_filters.join("/")}` : ""}
                        {focus.budget_max != null ? ` · do £${focus.budget_max}` : ""}
                        {data?.candidate_count ? ` · ${data.candidate_count} kandidátů prošlo filtrem` : ""}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {items.length > 0 && !running && (
                        <button type="button" data-testid="recruiter-save-btn"
                                onClick={async () => {
                                    try {
                                        await saveRecruiterList(focus.id);
                                        toast.success("Seznam uložen — najdeš ho v Analyze pod Uložené seznamy");
                                    } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
                                }}
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-fa-sky/50 text-fa-sky hover:bg-fa-sky hover:text-black flex items-center gap-1.5">
                            <FloppyDisk size={12} weight="bold" /> Uložit seznam
                        </button>
                    )}
                    <button type="button" onClick={run} disabled={running} data-testid="recruiter-run-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold disabled:opacity-50 flex items-center gap-1.5">
                        <Brain size={12} weight="bold" />
                        {running ? "AI analyzuje…" : items.length ? "Spustit znovu" : "Analyze Focus"}
                    </button>
                    <button type="button" onClick={onClose} className="p-1.5 text-white/40 hover:text-white" data-testid="recruiter-close">
                        <X size={14} />
                    </button>
                </div>
            </div>

            {running && (
                <div className="font-mono text-[10px] text-fa-mint animate-pulse" data-testid="recruiter-running">
                    AI prochází kandidáty, fixtures a fit skóre… (~20–40 s)
                </div>
            )}
            {data?.recruit_status === "failed" && (
                <div className="font-mono text-[10px] text-red-300" data-testid="recruiter-error">{data.recruit_error}</div>
            )}
            {data?.ai_summary && !running && (
                <p className="font-mono text-[11px] text-white/70 border-l-2 border-fa-mint pl-3 leading-relaxed" data-testid="recruiter-summary">
                    {data.ai_summary}
                </p>
            )}

            {items.length > 0 && !running && (
                <div className="grid md:grid-cols-2 gap-2.5" data-testid="recruiter-results">
                    {items.map((it) => (
                        <div key={it.id} className="border border-white/10 bg-[#0d0d0d] p-3 space-y-2" data-testid={`rec-card-${it.fpl_id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${CAT[it.recommendation_type]?.cls}`}>
                                        {CAT[it.recommendation_type]?.label}
                                    </span>
                                    <div className="font-display font-bold text-base mt-1.5">{it.name}</div>
                                    <div className="font-mono text-[9px] uppercase text-white/45">
                                        {it.team} · {it.position} · £{it.price} · {it.evidence?.ownership_pct}% own.
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-display font-black text-xl tabular-nums text-fa-mint">{Math.round(it.fit_score)}</div>
                                    <div className="font-mono text-[7px] uppercase text-white/35">Fit score</div>
                                    <div className="font-mono text-[8px] text-white/45 mt-1">conf. {Math.round(it.confidence)}%</div>
                                </div>
                            </div>
                            <div className="space-y-1 font-mono text-[10px] leading-relaxed">
                                <div className="text-fa-mint/90">+ {it.strengths}</div>
                                <div className="text-orange-300/80">− {it.risks}</div>
                                <div className="text-white/40">Horizont: {it.horizon}</div>
                            </div>
                            <div className="flex gap-2 pt-1 flex-wrap">
                                <button type="button" onClick={() => toCandidate(it)} data-testid={`rec-add-candidate-${it.fpl_id}`}
                                        className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black font-bold flex items-center gap-1">
                                    <Plus size={10} weight="bold" /> Kandidát
                                </button>
                                {it.in_pool ? (
                                    <button type="button" onClick={() => fromPool(it)} data-testid={`rec-remove-pool-${it.fpl_id}`}
                                            title="Vyřadit z poolu"
                                            className="font-mono text-[9px] uppercase px-2.5 py-1.5 border border-fa-mint/40 text-fa-mint hover:border-red-400/60 hover:text-red-300 transition-colors flex items-center gap-1 group/pool">
                                        <Check size={10} className="group-hover/pool:hidden" />
                                        <X size={10} className="hidden group-hover/pool:block" />
                                        <span className="group-hover/pool:hidden">V poolu</span>
                                        <span className="hidden group-hover/pool:inline">Vyřadit</span>
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => toPool(it)} data-testid={`rec-add-pool-${it.fpl_id}`}
                                            className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 bg-fa-mint text-black font-bold flex items-center gap-1">
                                        <Plus size={10} weight="bold" /> Do poolu
                                    </button>
                                )}
                                <Link to={`/players/${it.fpl_id}`} data-testid={`rec-open-${it.fpl_id}`}
                                      className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-white/15 text-white/60 hover:text-white flex items-center gap-1">
                                    <ArrowSquareOut size={10} /> Profil
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {!running && data && data.recruit_status !== "failed" && items.length === 0 && (
                <p className="font-mono text-[10px] text-white/45" data-testid="recruiter-empty">
                    Zatím žádná doporučení — spusť analýzu tlačítkem výše.
                </p>
            )}
        </div>
    );
}
